import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    fetchStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    createStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    updateStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle",// 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    error: null,
};

// Fetch recurring expense list
export const fetchAllRecurringExpense = createAsyncThunk('recurring/fetchAllRecurringExpense',
    async ({scopeId, roleSign, page, limit}) => {
        try {
            const params = new URLSearchParams();
            if (page) params.append("page", page);
            if (limit) params.append("limit", limit);

            const queryString = params.toString();
            const fullParams = queryString ? `&${queryString}` : '';

            const response = await api.get(`/${roleSign}/recurringExpense?id=${scopeId}${fullParams}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Create a new recurring expense
export const createRecurringExpense = createAsyncThunk('recurring/createRecurringExpense',
    async ({data, roleSign}) => {
        try {
            const response = await api.post(`/${roleSign}/recurringExpense`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch recurring expense by ID
export const fetchRecurringExpenseById = createAsyncThunk('recurring/fetchRecurringExpenseById',
    async ({roleSign, id}) => {
        try {
            const response = await api.get(`/${roleSign}/recurringExpense/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Update recurring expense
export const updateRecurringExpense = createAsyncThunk('recurring/updateRecurringExpense',
    async ({id, roleSign, data}) => {
        try {
            const response = await api.patch(`/${roleSign}/recurringExpense/${id}`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Delete recurring expense
export const deleteRecurringExpense = createAsyncThunk('recurring/deleteRecurringExpense',
    async ({roleSign, id}) => {
        try {
            const response = await api.delete(`/${roleSign}/recurringExpense/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

const recurringExpenseSlice = createSlice({
    name: "recurringExpense",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch recurring expense
            .addCase(fetchAllRecurringExpense.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchAllRecurringExpense.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchAllRecurringExpense.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Create recurring expense
            .addCase(createRecurringExpense.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createRecurringExpense.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createRecurringExpense.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch recurring expense by ID
            .addCase(fetchRecurringExpenseById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchRecurringExpenseById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchRecurringExpenseById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update recurring expense
            .addCase(updateRecurringExpense.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateRecurringExpense.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateRecurringExpense.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete recurring expense
            .addCase(deleteRecurringExpense.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteRecurringExpense.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteRecurringExpense.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetDeleteStatus,
    resetCreateStatus,
} = recurringExpenseSlice.actions;

export default recurringExpenseSlice.reducer;
